import { useMemo, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Stack, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useGetNovedadPendienteQuery } from '../../../store/api/blogApi';
import { puedeOfrecerse, anotarAparicion, darPorVista } from '../novedadEnBurbuja';
import { esRutaPublica } from '../../../lib/rutasPublicas';
import { useGetCustomerByIdQuery, useGetCustomerEventsQuery, useGetCustomerRemindersQuery } from '../../../store/api/customersApi';
import { useGetPropertyByIdQuery } from '../../../store/api/propertiesApi';
import { useGetTodayIndicatorQuery, useGetPendingIndicatorsQuery } from '../../../store/api/dailyIndicatorsApi';
import { useAppSelector } from '../../../hooks/useAppStore';
import { GustavoAvatarFab } from './GustavoAvatarFab';
import { useBolaArrastrable } from '../hooks/useBolaArrastrable';
import { useGetGustavoNoticesQuery } from '../../../store/api/gustavoApi';
import { useGetMyCreditsQuery } from '../../../store/api/creditsApi';
import { useGustavoNoticeLive } from '../useGustavoNoticeLive';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { avisoEnBurbuja } from '../avisoEnBurbuja';
import { waAMarkdown } from '../formatoAviso';
import { useEffectiveTenant } from '../../../hooks/useEffectiveTenant';
import { ANCLA_PORTALES } from '../../properties/anclaPortales';

const golden = P.brand.golden;

/** Un chip abre el chat con un prompt (`prompt`) o navega a una pantalla (`to`). */
interface Chip { label: string; prompt?: string; to?: string }
interface Ctx {
  key: string; msg: string; chips: Chip[]; novedad?: boolean; thumb?: string | null;
  /**
   * Burbuja de ACCIÓN, no de mensaje: mismo color de marca, pero borde doble, halo,
   * latido y el chip como botón sólido de ancho completo. Se reserva para lo que
   * BLOQUEA el trabajo —hoy, quedarse sin créditos—: si se usa para todo, deja de
   * destacar y vuelve a ser un mensaje más.
   */
  urgente?: boolean;
}

/** Un respiro al entrar a una pantalla: saltar encima del primer render se siente brusco. */
const MS_ARRANQUE   = 1_400;
/** Cada cuánto se re-chequea mientras el mouse está encima o la pestaña está de fondo. */
const MS_REINTENTO  = 400;
/** Cuánto tiene que estar en pantalla para que cuente como una de las 3 del día. */
const MS_CUENTA_APARICION = 4_000;

/**
 * El recordatorio del parte diario, ¿ocupa la burbuja de Gustavo?
 *
 * Hoy NO. Era la única otra cosa que aparecía sola ahí, y las dos juntas se turnaban: la
 * novedad se mostraba 15 s y se escondía 30, así que dos de cada tres veces que mirabas
 * la esquina no había nada — se leía como que estaba roto. El parte se sigue reclamando
 * donde corresponde (su pantalla, sus pendientes); lo que se sacó es su turno acá.
 *
 * Es un experimento: si el parte tiene que volver, alcanza con poner esto en `true`.
 */
const PARTE_EN_LA_BURBUJA = false;

/**
 * Si el parte vuelve a la burbuja: días de atraso a partir de los cuales deja de
 * recordarse igual. Quien acumula más que esto ya decidió no mandarlo — insistir no suma
 * un parte y sí gasta el único lugar proactivo que hay para hablarle.
 */
const DIAS_PARTE_SIN_INSISTIR = 4;

/** Sugerencia según la ruta actual (+ nombre de la entidad si lo tenemos en caché). */
function buildContext(path: string, custName: string, propLabel: string): Ctx | null {
  const cm = path.match(/^\/customers\/([a-f0-9]{24})/i);
  if (cm) {
    const nm = custName;
    return {
      key: 'cust:' + cm[1],
      msg: nm
        ? `Estás con ${nm}. ¿Querés que le escriba, agende una visita o anote algo en su ficha?`
        : '¿Necesitás algo con este cliente? Puedo escribirle, agendar una visita o anotar una nota.',
      chips: [
        { label: 'Redactar mensaje', prompt: nm ? `Ayudame a redactar un mensaje para ${nm}` : 'Ayudame a redactar un mensaje para este cliente' },
        { label: 'Agendar visita',   prompt: nm ? `Agendá una visita con ${nm}` : 'Agendá una visita con este cliente' },
        { label: 'Anotar nota',      prompt: nm ? `Anotá una nota en la ficha de ${nm}` : 'Anotá una nota en la ficha de este cliente' },
      ],
    };
  }
  if (/^\/customers/.test(path)) return {
    key: 'customers',
    msg: '¿Buscás a un cliente? Decime el nombre o el teléfono y lo encuentro.',
    chips: [{ label: 'Buscar cliente', prompt: 'Buscá un cliente por nombre o teléfono' }, { label: 'Sin contactar', prompt: '¿Qué clientes hace tiempo que no contacto?' }],
  };
  const pm = path.match(/^\/properties\/(?!nueva|mine)([A-Za-z0-9-]+)/);
  if (pm) {
    const lbl = propLabel;
    return {
      key: 'prop:' + pm[1],
      msg: lbl ? `Estás en “${lbl}”. ¿La publico, te genero el link oculto o cargo una visita?` : 'Estás en esta propiedad. ¿La publico, genero el link oculto o cargo una visita?',
      chips: [
        { label: 'Publicar',      prompt: `Publicá la propiedad ${pm[1]}` },
        { label: 'Link oculto',   prompt: `Generá el link oculto de la propiedad ${pm[1]}` },
        { label: 'Cargar visita', prompt: `Registrá una visita en la propiedad ${pm[1]}` },
      ],
    };
  }
  if (/^\/properties/.test(path)) return {
    key: 'properties',
    msg: '¿Buscás una propiedad o querés publicar una nueva? Decime.',
    chips: [{ label: 'Buscar propiedad', prompt: 'Buscá una propiedad' }, { label: 'Publicar nueva', prompt: 'Quiero publicar una propiedad nueva' }],
  };
  if (/^\/tasaciones/.test(path)) return {
    key: 'tasaciones',
    msg: '¿Vas a tasar? Puedo traerte comparables de la zona — pasame la dirección.',
    chips: [{ label: 'Buscar comparables', prompt: 'Buscá comparables para una tasación' }, { label: 'Nueva tasación', prompt: 'Quiero hacer una tasación nueva' }],
  };
  if (/^\/captaciones/.test(path)) return {
    key: 'captaciones',
    msg: '¿Seguimos con una captación? Puedo anotar el resultado o agendar el próximo contacto.',
    chips: [{ label: 'Anotar seguimiento', prompt: 'Anotá un seguimiento de una captación' }],
  };
  if (path === '/' || path.startsWith('/dashboard')) return {
    key: 'dashboard',
    msg: '¡Buenas! ¿Arrancamos? Puedo mostrarte tus pendientes de hoy y tus visitas.',
    chips: [{ label: 'Mis pendientes', prompt: 'Mostrame mis pendientes de hoy' }, { label: 'Mis visitas', prompt: 'Mostrame mis visitas de hoy' }],
  };
  return null;
}

// ── F4: sugerencias inteligentes (leen el estado real, sin IA/costo) ──
const EV_LABEL: Record<string, string> = { visit: 'una visita', call: 'una llamada', meeting: 'una reunión', showing: 'una muestra', offer: 'una oferta', inquiry: 'una consulta' };
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
const fmtDay = (d: Date) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const diff = Math.round((x.getTime() - t.getTime()) / 86_400_000);
  if (diff === 0) return 'hoy'; if (diff === 1) return 'mañana'; if (diff === -1) return 'ayer';
  return `el ${x.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' })}`;
};

interface EvLite { type: string; date: string }
interface RemLite { dueDate: string; isCompleted: boolean; title?: string }

/** Insight de cliente: sin teléfono → recordatorio vencido/hoy → evento próximo → hace X días sin contacto. */
function customerInsight(id: string, nm: string, phone: string | undefined, events?: EvLite[], reminders?: RemLite[]): Ctx | null {
  const who = nm || 'este cliente';
  const now = Date.now();
  const key = 'cust:' + id;
  const endToday = (() => { const t = new Date(); t.setHours(23, 59, 59, 999); return t.getTime(); })();

  // 0) sin teléfono (crítico: sin eso no hay WhatsApp ni contacto)
  if (!phone || !phone.trim()) {
    return {
      key, msg: `A ${who} le falta el teléfono cargado — sin eso no le podés escribir. ¿Lo agregamos?`,
      chips: [{ label: 'Cargar teléfono', prompt: `Agregá el teléfono de ${who}` }, { label: 'Anotar nota', prompt: `Anotá una nota en la ficha de ${who}` }],
    };
  }

  const pend = (reminders ?? []).filter((r) => !r.isCompleted).sort((a, b) => +new Date(a.dueDate) - +new Date(b.dueDate));
  const due = pend.find((r) => new Date(r.dueDate).getTime() <= endToday);
  if (due) {
    const overdue = new Date(due.dueDate).getTime() < now && !sameDay(new Date(due.dueDate), new Date());
    return {
      key, msg: `${overdue ? 'Tenés un recordatorio vencido' : 'Tenés un recordatorio para hoy'} con ${who}${due.title ? `: “${due.title}”` : ''}. ¿Lo resolvemos?`,
      chips: [{ label: 'Escribirle', prompt: `Ayudame a escribirle a ${who}` }, { label: 'Marcar hecho', prompt: `Marcá como completado el recordatorio de ${who}` }],
    };
  }
  const up = (events ?? []).filter((e) => new Date(e.date).getTime() > now).sort((a, b) => +new Date(a.date) - +new Date(b.date))[0];
  if (up) {
    return {
      key, msg: `Tenés ${EV_LABEL[up.type] ?? 'algo agendado'} con ${who} ${fmtDay(new Date(up.date))}. ¿Preparo algo?`,
      chips: [{ label: 'Anotar nota', prompt: `Anotá una nota para el próximo contacto con ${who}` }, { label: 'Recordatorio', prompt: `Ponme un recordatorio antes de la visita con ${who}` }],
    };
  }
  const past = (events ?? []).filter((e) => new Date(e.date).getTime() <= now).sort((a, b) => +new Date(b.date) - +new Date(a.date))[0];
  if (!past) {
    return {
      key, msg: `Todavía no registraste contacto con ${who}. ¿Le escribo o anotamos algo?`,
      chips: [{ label: 'Escribirle', prompt: `Ayudame a redactar un primer mensaje para ${who}` }, { label: 'Anotar nota', prompt: `Anotá una nota en la ficha de ${who}` }],
    };
  }
  const days = Math.floor((now - new Date(past.date).getTime()) / 86_400_000);
  if (days >= 4) {
    return {
      key, msg: `Hace ${days} días que no tenés contacto con ${who}. ¿Le escribo para retomar?`,
      chips: [{ label: 'Escribirle', prompt: `Ayudame a redactar un mensaje para retomar con ${who}` }, { label: 'Agendar visita', prompt: `Agendá una visita con ${who}` }],
    };
  }
  return null; // contacto reciente → cae al genérico
}

/** Piso de antigüedad del aviso para ofrecer republicar — el mismo que usa el widget
 *  de salud de publicaciones (MIN_DAYS_PUBLISHED en listingHealth.service). Antes de
 *  eso el aviso todavía es nuevo; a partir de ahí republicar sí rinde. */
const MIN_DIAS_AVISO = 15;

/**
 * Insight de propiedad: falta en un portal → días publicada sin cerrarse.
 *
 * Los días se cuentan sobre el AVISO, no sobre la ficha. Marisa Alegre lo reportó en la
 * encuesta del 18/08: republicaba una propiedad y al entrar a la ficha la burbuja le
 * seguía diciendo “lleva 349 días publicada, ¿la republico?” —contaba desde que la ficha
 * pasó a activa— así que se iba a MercadoLibre a chequear si la republicación había
 * salido. La sugerencia le hacía dudar de una acción que ya había hecho bien.
 */
export function propertyInsight(
  propId: string,
  label: string,
  prop?: { status?: string; createdAt?: string; statusHistory?: Array<{ to?: string; changedAt?: string }>; marketing?: { publishOnInfocasas?: boolean; meliId?: string; meliPublishedAt?: string; meliRepublishedAt?: string } },
  /**
   * ⛔ El hueco de portal depende del TENANT, no solo de la ficha. (14/09/2026)
   *
   * Infocasas es Imperium-only: el feed publica únicamente propiedades del tenant nativo
   * (ver `infocasas.service`), y por eso la tarjeta ya se le oculta a los Booster en
   * `PropertyActionsGrid` (`infocasasDisponible`). La burbuja no se había enterado y les
   * ofrecía "¿la incluyo en el feed?" de un portal con el que no tienen cuenta: el chip
   * manda a Gustavo a publicar en algo que no va a llegar a ningún lado.
   *
   * Para ellos el hueco que sí importa es MercadoLibre, y la acción vive en la card
   * "Portales inmobiliarios" de la misma ficha.
   */
  opts?: { infocasasDisponible?: boolean },
): Ctx | null {
  if (!prop || prop.status !== 'active') return null;
  const key = 'prop:' + propId;
  const name = label ? `“${label}”` : 'Esta propiedad';
  const infocasasDisponible = opts?.infocasasDisponible ?? true;

  // 0) hueco de portal: no está en Infocasas (solo donde Infocasas existe)
  if (infocasasDisponible && prop.marketing && prop.marketing.publishOnInfocasas === false) {
    return {
      key, msg: `${name} no está publicada en Infocasas. ¿La incluyo en el feed?`,
      chips: [{ label: 'Publicar en Infocasas', prompt: `Publicá la propiedad ${propId} en Infocasas` }, { label: 'Republicar', prompt: `Republicá la propiedad ${propId}` }],
    };
  }

  // 0-bis) sin Infocasas, el hueco de portal es MercadoLibre: sin aviso, no hay dónde verla.
  if (!infocasasDisponible && prop.marketing && !prop.marketing.meliId) {
    return {
      key, msg: `${name} no está publicada en MercadoLibre. ¿La publico?`,
      chips: [
        { label: 'Publicar en MercadoLibre', prompt: `Publicá la propiedad ${propId} en MercadoLibre` },
        // Lleva a la card donde está el control real, en esta misma ficha.
        { label: 'Portales inmobiliarios', to: `/properties/${propId}#${ANCLA_PORTALES}` },
      ],
    };
  }

  // Red por si el aviso no tiene fecha (los publicados antes de que existiera el campo):
  // republicada hace menos de MIN_DIAS_AVISO → no se vuelve a ofrecer, mida lo que mida
  // la antigüedad de la ficha.
  const republishedAt = prop.marketing?.meliRepublishedAt;
  if (republishedAt && Date.now() - new Date(republishedAt).getTime() < MIN_DIAS_AVISO * 86_400_000) return null;

  const firstActive = (prop.statusHistory ?? []).filter((h) => h.to === 'active').map((h) => h.changedAt).filter(Boolean).sort()[0] ?? prop.createdAt;
  // Si está publicada en ML, lo que importa es la antigüedad del aviso VIVO: republicar
  // cierra uno y abre otro, y el nuevo arranca de cero. Sin aviso, la ficha es lo único
  // que tenemos.
  const desde = (prop.marketing?.meliId && (prop.marketing.meliRepublishedAt ?? prop.marketing.meliPublishedAt)) || firstActive;
  if (!desde) return null;
  const days = Math.floor((Date.now() - new Date(desde).getTime()) / 86_400_000);
  if (days >= 90) return {
    key, msg: `${name} lleva ${days} días publicada sin cerrarse. ¿La republico o revisamos el precio?`,
    chips: [{ label: 'Republicar', prompt: `Republicá la propiedad ${propId}` }, { label: 'Revisar precio', prompt: `Analizá el precio de la propiedad ${propId} con comparables` }],
  };
  if (days >= MIN_DIAS_AVISO) return {
    key, msg: `${name} lleva ${days} días publicada. ¿Le damos un empujón?`,
    chips: [{ label: 'Republicar', prompt: `Republicá la propiedad ${propId}` }, { label: 'Destacar', prompt: `Destacá la propiedad ${propId}` }],
  };
  return null; // reciente → genérico
}

/**
 * Gustavo asistente — modo NO invasivo ("pull"): el avatar muestra un puntito
 * discreto cuando tiene una sugerencia para la pantalla actual, y la burbuja
 * aparece SOLO al pasar el mouse por encima (o queda disponible). Nada salta solo.
 * Click en el avatar = abre el chat; click en un chip = acción pre-cargada.
 */
export function GustavoAssistant({ onOpenChat }: { onOpenChat: () => void }) {
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const custId = path.match(/^\/customers\/([a-f0-9]{24})/i)?.[1];
  const propId = path.match(/^\/properties\/(?!nueva|mine)([A-Za-z0-9-]+)/)?.[1];

  // Avisos de Gustavo sin leer (correcciones de soporte, re-contactos): puntito en el
  // botón. Se refresca cada 2 min porque llegan sin que el agente haga nada. Apagarlo
  // NO es responsabilidad de acá: lo hace el widget al abrirse, así también se limpia
  // cuando el chat se abre por otra vía (atajo, evento externo).
  //
  // Tres vías para que el puntito aparezca, de mejor a peor: el evento en vivo por
  // Pusher (lo engancha useGustavoNoticeLive), el refetch al volver a la pestaña, y el
  // poll de fondo como último recurso si Pusher se cayó. Antes solo estaba el poll de
  // 2 minutos y en la práctica había que recargar la página entera para verlo.
  /**
   * Infocasas es Imperium-only (ver `propertyInsight`): a un Booster no se le ofrece un
   * portal con el que no tiene cuenta. Mismo criterio que la card de la ficha.
   */
  const { tenantMode } = useEffectiveTenant();
  const infocasasDisponible = tenantMode !== 'booster';

  useGustavoNoticeLive();
  const { data: notices } = useGetGustavoNoticesQuery(undefined, {
    pollingInterval: 120_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { data: customer }  = useGetCustomerByIdQuery(custId ?? '', { skip: !custId });
  const { data: property }  = useGetPropertyByIdQuery(propId ?? '', { skip: !propId });
  const { data: events }    = useGetCustomerEventsQuery(custId ?? '', { skip: !custId });
  const { data: reminders } = useGetCustomerRemindersQuery(custId ?? '', { skip: !custId });

  const custName = customer
    ? `${(customer as { firstName?: string; name?: string }).firstName ?? (customer as { name?: string }).name ?? ''} ${(customer as { lastName?: string }).lastName ?? ''}`.trim()
    : '';
  const propLabel = property ? ((property as { title?: string }).title || (property as { propertyId?: string }).propertyId || '') : '';

  // ── Parte diario: recordatorio proactivo en el dashboard SOLO si el agente no
  //    envió el de hoy o tiene partes atrasados. El resto de usuarios/rutas no lo ve.
  const userRole = useAppSelector((s) => s.auth.user?.role);
  const userId   = useAppSelector((s) => s.auth.user?._id) ?? '';
  const onDashboard = path === '/' || path.startsWith('/dashboard');
  const parteEnabled = PARTE_EN_LA_BURBUJA && onDashboard && userRole === 'agent';
  const { data: today } = useGetTodayIndicatorQuery(undefined, { skip: !parteEnabled });
  const { data: pendings } = useGetPendingIndicatorsQuery(undefined, { skip: !parteEnabled });

  const parteCtx = useMemo<Ctx | null>(() => {
    if (!parteEnabled || !today) return null;
    const nPend = pendings?.length ?? 0;
    const sentToday = today.status === 'submitted';
    // Ya enviaste el parte de HOY → no volver a mostrar hasta que cierre el día
    // operativo (mañana el open window trae un doc nuevo sin enviar y reaparece).
    if (sentToday) return null;
    // Arrastra más de 4 días sin enviarlo: el recordatorio ya demostró que no funciona
    // con esta persona, y repetirlo un día más solo entrena a ignorar la burbuja
    // entera —que es la que ahora tiene que ofrecer las novedades—. El parte sigue
    // reclamándose por sus vías (el pendiente no se borra, la pantalla lo muestra);
    // lo que se libera es el lugar proactivo del dashboard.
    if (nPend > DIAS_PARTE_SIN_INSISTIR) return null;
    const msg = nPend > 0
      ? `Tenés ${nPend} parte${nPend > 1 ? 's' : ''} sin enviar. ¿Te muestro tu producción y lo enviamos?`
      : 'Todavía no enviaste tu parte de hoy. ¿Te muestro tu producción del día y lo enviamos?';
    return {
      key: `parte:${today.operationalDate}:${nPend}:${sentToday ? 'sent' : 'open'}`,
      msg,
      chips: [
        { label: 'Ver mi parte', prompt: 'Mostrame mi parte diario' },
        { label: nPend > 0 ? 'Enviar pendientes' : 'Enviar mi parte', prompt: 'Enviá mi parte diario' },
      ],
    };
  }, [parteEnabled, today, pendings]);

  const ctx = useMemo(() => {
    // Prioridad en el dashboard: recordatorio del parte diario si corresponde.
    if (parteCtx) return parteCtx;
    // F4: primero intenta una sugerencia inteligente (estado real); si no hay, cae al genérico.
    if (custId) return customerInsight(custId, custName, (customer as { phone?: string } | undefined)?.phone, events as EvLite[] | undefined, reminders as RemLite[] | undefined) ?? buildContext(path, custName, propLabel);
    if (propId) return propertyInsight(propId, propLabel, property as Parameters<typeof propertyInsight>[2], { infocasasDisponible }) ?? buildContext(path, custName, propLabel);
    return buildContext(path, custName, propLabel);
  }, [parteCtx, path, custName, propLabel, custId, propId, events, reminders, property, customer, infocasasDisponible]);

  // ── Novedad sin leer: la razón de ser de esta burbuja ──
  // Las novedades son tutoriales de cómo hacer las cosas en el CRM y no estaban
  // llegando: 20 lectores promedio sobre 145 agentes activos (14%). El puntito del menú
  // no alcanza — hay que acordarse de ir a mirarlo. Gustavo ya está a la vista en todas
  // las pantallas, así que la ofrece él.
  // Dos pantallas donde NO se ofrece:
  //  · Novedades: ahí ya está leyendo.
  //  · el sitio público — Gustavo se renderiza igual para un agente logueado que navega
  //    la web de cara al cliente, y un tutorial interno no pinta nada ahí.
  const enNovedades = path.startsWith('/blog') || esRutaPublica(path);
  const { data: pendiente, refetch: refetchPendiente } = useGetNovedadPendienteQuery(undefined, {
    skip: !userId,
    // Se publican pocas por semana: no hace falta más. El backend además cachea la
    // respuesta 60 s por usuario, así que la mayoría de estos pedidos no toca la base.
    pollingInterval: 300_000,
    refetchOnFocus: true,
  });
  const novedad = pendiente?.post ?? null;

  // Leyó una y volvió al CRM → preguntar cuál es la próxima que le falta. Así se
  // encadenan: la última sin leer, la anterior, y así hasta que no queda ninguna y
  // Gustavo deja de ofrecer. Sin esto había que esperar al poll para ver la siguiente.
  // Puede que el backend todavía conteste la misma (cachea 60 s): no se muestra igual,
  // porque abrirla ya la dejó marcada de este lado.
  const veniaDeNovedades = useRef(enNovedades);
  useEffect(() => {
    if (veniaDeNovedades.current && !enNovedades) refetchPendiente();
    veniaDeNovedades.current = enNovedades;
  }, [enNovedades, refetchPendiente]);

  // OJO: acá NO se consulta el cupo de apariciones. Esto es solo "hay una novedad para
  // ofrecer"; si CORRESPONDE mostrarla ahora lo decide la rotación, una sola vez, al
  // empezar el turno. Cuando el cupo se miraba acá, cualquier re-render que trajera un
  // objeto nuevo de la query —un refresco al volver el foco, o simplemente llegar los
  // datos del parte— volvía a leerlo con la aparición YA contada y la burbuja se
  // borraba sola a los milisegundos de aparecer. (visto en el dashboard, 05/08)
  const novedadCtx = useMemo<Ctx | null>(() => {
    if (!novedad || enNovedades || !userId) return null;
    return {
      key: `nov:${novedad._id}`,
      novedad: true,
      // La portada hace la mitad del trabajo: un bloque de texto en una esquina se
      // ignora, una imagen con un título se mira. Toda novedad lleva portada.
      thumb: novedad.imageUrl,
      msg: `Salió una novedad que todavía no leíste: “${novedad.title}”. ¿La abrimos?`,
      // `?ref=gustavo` separa las lecturas que vienen de acá de las del menú: sin eso no
      // hay forma de saber si la burbuja sumó lectores o solo adelantó a los de siempre.
      chips: [{ label: 'Leerla ahora', to: `/blog/${novedad._id}?ref=gustavo` }],
    };
  }, [novedad, enNovedades, userId]);

  /**
   * El AVISO de Gustavo en la burbuja: lo que él le mandó a ESTA persona.
   *
   * El preview ya se guardaba y ya llegaba hasta acá, pero se usaba solo como `title` del
   * HTML: el tooltip nativo, que hay que descubrir con el mouse y en el celular no existe.
   * El agente veía un puntito rojo y nada más — para enterarse tenía que entrar.
   *
   * Va POR ENCIMA de la novedad y de la sugerencia contextual, y es la única de las tres
   * que se justifica sola: una novedad puede esperar a mañana y una sugerencia es opcional
   * por definición, pero "te devolvimos 26 créditos" o "un cliente te espera hace un día"
   * es información dirigida a esa persona, sobre su trabajo, y caduca.
   *
   * Tampoco tiene el tope de 3 apariciones por día de las novedades: una novedad es algo
   * que se OFRECE, un aviso es algo que ya se le mandó. Se muestra hasta que lo lee, y el
   * mecanismo de leído ya existe — es el mismo que apaga el puntito.
   * (pedido de dirección 13/08/2026)
   */
  /**
   * El armado vive en `avisoEnBurbuja` y no acá: la burbuja tiene 320 px y el aviso se salía
   * por el costado cuando el texto traía un link, que no tiene espacios y el navegador no
   * puede cortar. Ahí se le sacan los enlaces y se recorta con puntos suspensivos.
   */
  const avisoCtx = useMemo<Ctx | null>(() => avisoEnBurbuja(notices), [notices]);

  /**
   * ⛔ SIN CRÉDITOS, GUSTAVO NO HACE NADA — Y EL AGENTE NO SE ENTERA DE QUE PUEDE COMPRAR.
   *
   * (24/08/2026, pedido de dirección) `LowBalanceBanner` ya avisaba, pero vive DENTRO del
   * chat: el que está en cero no lo abre —Gustavo le contesta que está pausado— así que
   * nunca ve el camino para recargar. La burbuja del botón, en cambio, la ve sin abrir
   * nada y en todas las pantallas. Medido ese día: 28 wallets en cero y 12 abajo de 40, y
   * en la encuesta del 18/08 los que peor puntuaban a Gustavo eran justo los que no lo
   * podían usar.
   *
   * Va ARRIBA del aviso y de la novedad, que es lo único que se pone por encima de un
   * aviso dirigido: si no puede usarlo, ofrecerle un tutorial de cómo usarlo es absurdo,
   * y el aviso que le llegó tampoco lo va a poder responder.
   *
   * ⛔ NO lo manda a pedirle créditos a su office_admin: no se los puede dar. La plata la
   * pone la empresa y solo el admin del tenant los otorga. Las dos vías reales son la
   * cuota que se renueva sola el 1° y pedir un paquete en el Marketplace.
   */
  const { data: creditos } = useGetMyCreditsQuery(undefined, { skip: !userId });
  const sinCreditosCtx = useMemo<Ctx | null>(() => {
    if (!creditos || creditos.unlimited || !creditos.systemEnabled) return null;
    if (creditos.balance > 0) return null;
    // En el sitio público no: Gustavo se renderiza igual para un agente logueado que
    // navega la web de cara al cliente, y esto es de adentro.
    if (esRutaPublica(path)) return null;
    return {
      key: 'sin-creditos',
      urgente: true,
      msg: 'Te quedaste sin créditos, así que por ahora no te puedo ayudar. Tu cuota se '
        + 'renueva sola el 1°; si no querés esperar, pedí un paquete y seguimos hoy mismo.',
      chips: [{ label: 'Comprar créditos', to: '/marketplace' }],
    };
  }, [creditos, path]);

  const [open, setOpen] = useState(false);         // burbuja por hover
  const [autoOpen, setAutoOpen] = useState(false); // burbuja contextual proactiva
  const [autoNov, setAutoNov]   = useState(false); // turno de la novedad en la rotación
  /** La portada no cargó (S3 caído, URL vieja): se sigue sin ella, no se rompe la burbuja. */
  const [thumbRota, setThumbRota] = useState(false);
  const leaveT = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissedKeys = useRef<Set<string>>(new Set());
  /** El mouse encima congela la rotación: si empezó a leer, no se la sacamos. */
  const hoverRef = useRef(false);

  /**
   * ⛔ LA SUGERENCIA DE CONTEXTO YA NO SE ABRE SOLA sobre una ficha.
   *
   * Germán Montero (03/09/2026): "hay posibilidades de hacer que el avatar de Gustavo sea
   * movil? a veces ando desde la laptop y la pantalla me queda medio chica y me tapa
   * algunas opciones". Lo que le tapaba no era el avatar —60 px en la esquina— era ESTA
   * burbuja abriéndose sobre la ficha de la propiedad: en su pantalla le comía el teléfono
   * del cliente y la fila de Exclusividad.
   *
   * La misma burbuja se apagó en Booster el 02/09 (`BoosterGustavoFab`, MOSTRAR_SUGERENCIA):
   * acá quedó prendida porque la arma otro componente, y el agente lo reportó desde el otro
   * lado. Se apaga el mismo criterio en los dos lugares.
   *
   * Lo que NO se toca, porque no es lo que tapa nada:
   *  · la burbuja al pasar el mouse por el avatar — la sugerencia sigue disponible;
   *  · los avisos reales de Gustavo, las novedades y el aviso de sin créditos, que se
   *    sostienen por su cuenta (`mostrandoAviso`, `mostrandoNovedad`, `mostrandoSinCreditos`);
   *  · la rama del PARTE DIARIO, que sale en el dashboard —donde no hay formulario debajo—.
   *    Hoy es inerte porque `PARTE_EN_LA_BURBUJA` ya está en false por otra decisión; se
   *    deja para que la condición siga siendo la correcta si el parte vuelve a la burbuja.
   *
   * Volver a prenderla es cambiar este `false`.
   */
  const SUGERENCIA_PROACTIVA = false;
  const isAutoRoute = !!ctx && (
    ctx.key.startsWith('parte:')
    || (SUGERENCIA_PROACTIVA && (ctx.key === 'customers' || ctx.key.startsWith('prop:')))
  );

  useEffect(() => {
    setAutoOpen(false); // al cambiar de contexto, reseteo
    if (!ctx || !isAutoRoute || dismissedKeys.current.has(ctx.key)) return;
    const t = setTimeout(() => {
      setAutoOpen(true);
      window.dispatchEvent(new CustomEvent('gustavo:nudge')); // que Gustavo salude al aparecer
    }, 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.key, isAutoRoute]);

  /**
   * La novedad aparece y SE QUEDA hasta que el agente hace algo con ella.
   *
   * Antes se turnaba con la sugerencia de la pantalla —15 s ella, 30 s la otra—, y el
   * resultado fue que dos de cada tres veces que uno miraba la esquina no había nada.
   * Una novedad que se esconde sola no la lee nadie: es lo que estamos tratando de
   * arreglar, no algo para dosificar. Se va cuando la abre, cuando la cierra con la X,
   * o cuando entra a Novedades — las tres son decisiones suyas.
   *
   * No depende de la ruta a propósito: si dependiera, cada navegación la reiniciaría.
   */
  const novedadId = novedadCtx ? novedad!._id : null;
  useEffect(() => {
    setThumbRota(false); // otra novedad, otra portada: se le da una chance nueva
    if (!novedadId || !userId || !puedeOfrecerse(userId, novedadId)) { setAutoNov(false); return; }
    let vivo = true;
    let t: ReturnType<typeof setTimeout> | undefined;
    let tCuenta: ReturnType<typeof setTimeout> | undefined;

    /**
     * Espera el respiro inicial. Dos cosas lo posponen en vez de dejarlo pasar:
     *  · el mouse encima (está mirando la burbuja anterior, no se le cambia debajo);
     *  · la pestaña en segundo plano — aparecer donde nadie mira gastaría una de las
     *    tres del día.
     */
    const cuandoSePueda = (ms: number, fn: () => void) => {
      t = setTimeout(function chequear() {
        if (!vivo) return;
        if (hoverRef.current || document.visibilityState === 'hidden') {
          t = setTimeout(chequear, MS_REINTENTO);
          return;
        }
        fn();
      }, ms);
    };

    cuandoSePueda(MS_ARRANQUE, () => {
      setAutoNov(true);
      window.dispatchEvent(new CustomEvent('gustavo:nudge')); // que Gustavo salude
      // La aparición se anota recién a los segundos de estar en pantalla, no al
      // mostrarla. Contándola de entrada, recargar la página gastaba una de las tres
      // sin que el agente hubiera llegado a leer nada — con dos recargas se quedaba sin
      // novedad por el resto del día. Si navega o recarga antes, este timer muere con
      // el efecto y no se cuenta.
      tCuenta = setTimeout(() => anotarAparicion(userId, novedadId), MS_CUENTA_APARICION);
    });

    return () => { vivo = false; clearTimeout(t); clearTimeout(tCuenta); };
  }, [novedadId, userId]);

  /**
   * Cuál de las tres se está viendo. El AVISO gana siempre: es lo único dirigido a esta
   * persona y lo único que caduca. Después la novedad durante su turno, y al final la
   * sugerencia de la pantalla.
   */
  /**
   * La X cierra el aviso, sin darlo por leído.
   *
   * `bubbleOpen` incluye `mostrandoAviso`, así que mientras hubiera un aviso sin leer la
   * burbuja volvía sola y el botón no hacía NADA: `dismiss` apagaba `open` y `autoOpen`,
   * que no son los que la sostenían. El admin de Lion lo reportó como "no funciona la X".
   * (14/08/2026)
   *
   * Se oculta y no se marca como leído a propósito: apretar la X no es leer el aviso, y el
   * aviso es lo único de los tres que está dirigido a esa persona y caduca. El puntito
   * sigue prendido y lo vuelve a ver al recargar — lo que se respeta es "ahora no", no
   * "ya está".
   *
   * La marca se guarda contra la CLAVE del aviso: si llega uno nuevo, vuelve a aparecer.
   */
  const [avisoOculto, setAvisoOculto] = useState<string | null>(null);
  /**
   * La X sobre "sin créditos" es "ahora no", igual que en el aviso: no se marca nada del
   * lado del servidor y vuelve al recargar. Descartarla para siempre dejaría al agente
   * sin el único camino visible para recargar.
   */
  const [sinCreditosOculto, setSinCreditosOculto] = useState(false);
  const mostrandoSinCreditos = !!sinCreditosCtx && !sinCreditosOculto;
  const mostrandoAviso = !!avisoCtx && avisoOculto !== avisoCtx.key;
  const mostrandoNovedad = !mostrandoAviso && autoNov && !!novedadCtx;
  /**
   * La PRIORIDAD vive acá y en ningún otro lado: sin créditos gana sobre todo, después el
   * aviso dirigido, después la novedad, y al final la sugerencia de la pantalla.
   * (Repetirla en los `mostrando*` no cambiaba nada y dejaba el mismo criterio en dos
   * lugares, que es cómo empiezan los arreglos a medias.)
   */
  const activo: Ctx | null = mostrandoSinCreditos
    ? sinCreditosCtx
    : (mostrandoAviso ? avisoCtx : (mostrandoNovedad ? novedadCtx : ctx));

  const reveal = () => { hoverRef.current = true; clearTimeout(leaveT.current); if (activo) setOpen(true); };
  const scheduleHide = () => { hoverRef.current = false; leaveT.current = setTimeout(() => setOpen(false), 160); };

  // Cerrar con la X o al disparar una acción: cierra ambas y no vuelve a auto-aparecer en esa pantalla.
  // Sobre la novedad la X es definitiva: la descartó, no se le insiste más.
  const dismiss = () => {
    setOpen(false);
    // Se sostiene sola, como el aviso: hay que apagarla por su lado.
    if (mostrandoSinCreditos) { setSinCreditosOculto(true); return; }
    // El aviso se sostiene solo, así que hay que apagarlo por su lado.
    if (mostrandoAviso && avisoCtx) { setAvisoOculto(avisoCtx.key); return; }
    if (mostrandoNovedad && novedadId) { darPorVista(userId, novedadId); setAutoNov(false); return; }
    setAutoOpen(false);
    if (ctx) dismissedKeys.current.add(ctx.key);
  };

  const fire = (chip: Chip) => {
    if (chip.to) {
      // Abrirla cuenta como leída: el backend se entera recién cuando entra el
      // page_view y el cache de 60 s la seguiría ofreciendo hasta entonces.
      if (novedadId) darPorVista(userId, novedadId);
      setOpen(false); setAutoNov(false);
      navigate(chip.to);
      return;
    }
    // El aviso: se abre el chat sin mandar ningún prompt. No hace falta decirle a qué
    // conversación ir — el widget ya manda al hilo del aviso cuando hay alguno sin leer, y
    // los marca como leídos al abrirse.
    if (chip.prompt === '__abrir_avisos__') {
      setOpen(false);
      window.dispatchEvent(new CustomEvent('gustavo:open'));
      return;
    }
    dismiss();
    window.dispatchEvent(new CustomEvent('gustavo:open', { detail: { prompt: chip.prompt } }));
  };

  const bubbleOpen = !!activo && (mostrandoSinCreditos || mostrandoAviso || mostrandoNovedad || open || autoOpen);

  // Se puede arrastrar a otro rincón; la burbuja se abre hacia el centro de la pantalla.
  const { pos, arrastrando, manija } = useBolaArrastrable();
  const aLaIzquierda = pos?.lado === 'left';
  const arriba = pos?.vert === 'top';

  return (
    <Box
      onPointerEnter={reveal}
      onPointerLeave={scheduleHide}
      sx={{
        position: 'fixed',
        ...(pos
          ? { [pos.lado]: pos.h, [pos.vert]: pos.v }
          : { bottom: { xs: 16, md: 24 }, right: { xs: 16, md: 24 } }),
        zIndex: (t) => t.zIndex.drawer - 1,
        display: 'flex', flexDirection: arriba ? 'column-reverse' : 'column',
        alignItems: aLaIzquierda ? 'flex-start' : 'flex-end', gap: bubbleOpen ? 1.25 : 0,
      }}
    >
      {/* Burbuja (solo al pasar el mouse) */}
      {bubbleOpen && (() => {
      /* ⛔ El acento SIEMPRE es el de la marca (`P.brand.golden`), que se muta in-place
         con el color del tenant: dorado en Imperium, el primario en Booster, y el suyo en
         un white-label. Un color propio acá se vería como de otra aplicación justo en la
         pantalla donde le pedimos plata. Lo que despega a esta burbuja del resto NO es el
         tono, es el PESO: borde doble, halo, latido y el chip como botón sólido. */
      const urg = !!activo!.urgente;
      const ac  = golden;
      return (
        <Box sx={{
          maxWidth: urg ? 340 : 320, transformOrigin: `${arriba ? 'top' : 'bottom'} ${aLaIzquierda ? 'left' : 'right'}`,
          animation: 'gvbub-in .42s cubic-bezier(.34,1.56,.64,1)',
          '@keyframes gvbub-in':  { from: { opacity: 0, transform: 'translateY(12px) scale(.7)' }, to: { opacity: 1, transform: 'none' } },
          '@keyframes gvchip-in': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
        }}>
          <Box sx={{
            position: 'relative', background: 'linear-gradient(160deg,#1b1710,#141210)',
            border: `${urg ? 2 : 1}px solid ${alpha(ac, urg ? 0.75 : 0.3)}`,
            borderRadius: 3,
            [`border${arriba ? 'Top' : 'Bottom'}${aLaIzquierda ? 'Left' : 'Right'}Radius`]: 6,
            p: 1.75,
            /**
             * ⛔ NADA SE SALE DEL GLOBO. El texto ya viene recortado y sin links
             * (`avisoEnBurbuja`), pero esto es la red: cualquier palabra sin espacios que
             * llegue por otro lado se parte en vez de empujar el borde. `overflow: hidden`
             * NO va acá — recortaría la flecha del `::after`, que vive fuera de la caja.
             */
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            boxShadow: urg ? `0 18px 44px rgba(0,0,0,.55), 0 0 0 4px ${alpha(ac, 0.10)}` : '0 18px 44px rgba(0,0,0,.55)',
            ...(urg && {
              animation: 'gvurg-pulse 2.6s ease-in-out infinite',
              '@keyframes gvurg-pulse': {
                '0%,100%': { boxShadow: `0 18px 44px rgba(0,0,0,.55), 0 0 0 4px ${alpha(ac, 0.08)}` },
                '50%':     { boxShadow: `0 18px 44px rgba(0,0,0,.55), 0 0 0 9px ${alpha(ac, 0.02)}` },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
            '&::after': {
              // La flecha apunta a la bola, esté donde esté.
              content: '""', position: 'absolute', [aLaIzquierda ? 'left' : 'right']: 22, [arriba ? 'top' : 'bottom']: -7, width: 14, height: 14,
              background: '#141210', borderRight: `1px solid ${alpha(ac, 0.3)}`, borderBottom: `1px solid ${alpha(ac, 0.3)}`,
              transform: arriba ? 'rotate(225deg)' : 'rotate(45deg)',
            },
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.6}>
                {/* El megáfono avisa de un vistazo que esto NO es la sugerencia de la
                    pantalla: es algo que salió y el agente todavía no vio. */}
                {activo!.novedad && <CampaignRoundedIcon sx={{ fontSize: 14, color: ac }} />}
                {urg && <BoltRoundedIcon sx={{ fontSize: 15, color: ac }} />}
                <Typography sx={{ fontSize: urg ? 10.5 : 10, letterSpacing: '.14em', textTransform: 'uppercase', color: ac, fontWeight: urg ? 800 : 700 }}>
                  {urg ? 'Sin créditos' : (activo!.novedad ? 'Gustavo · Novedad' : 'Gustavo')}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={dismiss}
                aria-label={activo!.novedad ? 'No mostrarme más esta novedad' : 'Cerrar sugerencia'}
                sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: '#fff' } }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              {/* Miniatura de la portada. Se puede clickear igual que el chip: en una
                  tarjeta con imagen, la imagen ES el botón que la gente busca.
                  Si la novedad no tuviera portada, o si el archivo no carga, la fila
                  queda como estaba — el texto nunca depende de que la imagen exista. */}
              {activo!.thumb && !thumbRota && (
                <Box
                  component="img"
                  src={activo!.thumb}
                  alt=""
                  onError={() => setThumbRota(true)}
                  onClick={() => fire(activo!.chips[0])}
                  sx={{
                    width: 54, height: 54, flexShrink: 0, cursor: 'pointer',
                    borderRadius: 1.5, objectFit: 'cover', display: 'block',
                    border: `1px solid ${alpha(ac, 0.28)}`, bgcolor: alpha(ac, 0.08),
                    transition: 'transform .15s, border-color .15s',
                    '&:hover': { transform: 'scale(1.03)', borderColor: alpha(ac, 0.6) },
                  }}
                />
              )}
              {/* Los avisos de Gustavo vienen con formato de WhatsApp (`*negrita*`) porque el
                  mismo texto sale por ahí. Se traduce a Markdown y se renderiza: en plano se
                  veían los asteriscos crudos. El `strong` va en dorado, igual que en el
                  widget de la home. */}
              <Typography component="div" sx={{
                fontSize: 13.5, lineHeight: 1.5, color: '#F0EDE6',
                // En una fila flex el texto no se encoge por debajo de su contenido salvo
                // que se le diga: sin esto, junto a la miniatura empujaba el borde derecho.
                minWidth: 0, flex: 1, overflowWrap: 'anywhere',
                '& p': { m: 0 },
                '& p + p': { mt: 0.75 },
                '& strong': { color: ac, fontWeight: 700 },
                '& a': { color: ac },
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {waAMarkdown(activo!.msg)}
                </ReactMarkdown>
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.25}>
              {activo!.chips.map((c, i) => (
                <Box key={c.label} component="button" onClick={() => fire(c)} sx={{
                  font: 'inherit', cursor: 'pointer',
                  ...(urg ? {
                    // Sólido y de ancho completo: un llamado a la acción, no una opción más.
                    flex: 1, fontSize: 13.5, fontWeight: 800, px: 2, py: 1, borderRadius: 2,
                    color: '#14110B', bgcolor: ac, border: `1px solid ${ac}`,
                  } : {
                    fontSize: 12.5, fontWeight: 600,
                    px: 1.25, py: 0.65, borderRadius: 5, color: ac,
                    bgcolor: alpha(ac, 0.12), border: `1px solid ${alpha(ac, 0.25)}`,
                  }),
                  opacity: 0, animation: 'gvchip-in .4s cubic-bezier(.2,.8,.2,1) both', animationDelay: `${120 + i * 80}ms`,
                  transition: 'background-color .15s, transform .15s',
                  '&:hover': { bgcolor: urg ? alpha(ac, 0.88) : alpha(ac, 0.22), transform: 'translateY(-1px)' },
                }}>{c.label}</Box>
              ))}
            </Stack>
          </Box>
        </Box>
      );
      })()}

      {/* Avatar (con puntito si hay sugerencia y la burbuja está cerrada) */}
      <Box {...manija} title="Arrastrame para moverme" sx={{ touchAction: 'none', cursor: arrastrando ? 'grabbing' : undefined }}>
      <GustavoAvatarFab
        onClick={onOpenChat}
        hint={!!activo && !bubbleOpen}
        notices={notices?.count ?? 0}
        noticePreview={notices?.preview}
      />
      </Box>
    </Box>
  );
}
